import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useLockBodyScrolling } from "../../../common/util/cssUtils";
import defaultContent from "../../../content/periodSelector";
import "./PeriodDrawer.scss";

export type PeriodGroup = {
  label: string;
  periods: string[];
};

type PeriodDrawerProps = {
  groups: PeriodGroup[];
  selectedPeriod: string;
  onSelect: (period: string) => void;
  onClose: () => void;
  formatPeriod: (period: string) => string;
};

export const PeriodDrawer = ({ groups, selectedPeriod, onSelect, onClose, formatPeriod }: PeriodDrawerProps) => {
  const { t } = useTranslation(["periodSelector"]);
  const [isOpen, setIsOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragCurrentY = useRef(0);
  const isDragging = useRef(false);

  const allPeriods = groups.flatMap((g) => g.periods);

  const [focusedIndex, setFocusedIndex] = useState(() => {
    const idx = allPeriods.indexOf(selectedPeriod);
    return idx >= 0 ? idx : 0;
  });

  useLockBodyScrolling(true, sheetRef);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsOpen(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!listRef.current) return;
    const activeEl = listRef.current.querySelector(".PeriodDrawer__item--active");
    activeEl?.scrollIntoView({ block: "center" });
  }, [isOpen]);

  // Focus trap: keep focus inside the drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      }
      if (e.key === "Tab" && sheetRef.current) {
        const focusableEls = sheetRef.current.querySelectorAll<HTMLElement>(
          '[role="option"], button, [tabindex]:not([tabindex="-1"])',
        );
        if (focusableEls.length === 0) return;
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus the active option when drawer opens
  useEffect(() => {
    if (!isOpen || !listRef.current) return;
    requestAnimationFrame(() => {
      const activeEl = listRef.current?.querySelector<HTMLElement>(".PeriodDrawer__item--active");
      activeEl?.focus();
    });
  }, [isOpen]);

  const close = () => {
    setIsOpen(false);
    setTimeout(onClose, 280);
  };

  const handleSelect = (period: string) => {
    setIsOpen(false);
    setTimeout(() => onSelect(period), 280);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    dragStartY.current = e.touches[0].clientY;
    dragCurrentY.current = 0;
    isDragging.current = true;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging.current || !sheetRef.current) return;
    const delta = e.touches[0].clientY - dragStartY.current;
    if (delta > 0) {
      dragCurrentY.current = delta;
      sheetRef.current.style.transform = `translateY(${delta}px)`;
      sheetRef.current.style.transition = "none";
    }
  };

  const handleTouchEnd = () => {
    if (!sheetRef.current) return;
    isDragging.current = false;
    sheetRef.current.style.transform = "";
    sheetRef.current.style.transition = "";
    if (dragCurrentY.current > 120) {
      close();
    }
  };

  const focusOptionByIndex = useCallback((index: number) => {
    if (!listRef.current) return;
    const options = listRef.current.querySelectorAll<HTMLElement>('[role="option"]');
    options[index]?.focus();
    options[index]?.scrollIntoView({ block: "nearest" });
  }, []);

  const handleOptionKeyDown = (e: React.KeyboardEvent, period: string, flatIndex: number) => {
    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = Math.min(flatIndex + 1, allPeriods.length - 1);
        setFocusedIndex(next);
        focusOptionByIndex(next);
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = Math.max(flatIndex - 1, 0);
        setFocusedIndex(prev);
        focusOptionByIndex(prev);
        break;
      }
      case "Home": {
        e.preventDefault();
        setFocusedIndex(0);
        focusOptionByIndex(0);
        break;
      }
      case "End": {
        e.preventDefault();
        setFocusedIndex(allPeriods.length - 1);
        focusOptionByIndex(allPeriods.length - 1);
        break;
      }
      case "Enter":
      case " ": {
        e.preventDefault();
        handleSelect(period);
        break;
      }
    }
  };

  let flatIndex = 0;

  return (
    <>
      <div
        className={`PeriodDrawer__backdrop${isOpen ? " PeriodDrawer__backdrop--visible" : ""}`}
        onClick={close}
        role="presentation"
      />
      <div
        ref={sheetRef}
        className={`PeriodDrawer__sheet${isOpen ? " PeriodDrawer__sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={t("selectPeriod", defaultContent["selectPeriod"])}
        data-testid="period-selector-drawer"
      >
        <div
          className="PeriodDrawer__handle-area"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          <div className="PeriodDrawer__handle" />
        </div>

        <div
          ref={listRef}
          className="PeriodDrawer__list"
          role="listbox"
          aria-label={t("bonusPeriodList", defaultContent["bonusPeriodList"])}
          data-testid="period-selector-drawer-list"
        >
          {groups.map((group) => (
            <div
              key={group.label || "default"}
              className="PeriodDrawer__group"
              role="group"
              aria-label={group.label || undefined}
            >
              {group.label && (
                <div className="PeriodDrawer__group-header" role="presentation">
                  {group.label}
                </div>
              )}
              {group.periods.map((period) => {
                const isActive = period === selectedPeriod;
                const currentFlatIndex = flatIndex++;
                return (
                  <div
                    key={period}
                    role="option"
                    aria-selected={isActive}
                    tabIndex={currentFlatIndex === focusedIndex ? 0 : -1}
                    className={`PeriodDrawer__item${isActive ? " PeriodDrawer__item--active" : ""}`}
                    onClick={() => handleSelect(period)}
                    onKeyDown={(e) => handleOptionKeyDown(e, period, currentFlatIndex)}
                    data-testid={`period-drawer-item-${period}`}
                    data-value={period}
                  >
                    {formatPeriod(period)}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </>
  );
};
